import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Loader2, Search } from "lucide-react";
import { tarifasApi, errMsg, fmtMoney, tierLabel } from "./api";
import { OwnerBadge } from "./ProposalCard";
import { looksLikeKimkimRef } from "./ActivityPicker";

const ALL = "__all__";
const NONE = "__none__";

export const KimkimCatalog = ({ providers, refreshKey }) => {
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState(ALL);
  const [q, setQ] = useState("");
  const [looking, setLooking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setActs(await tarifasApi.activities({}));
    } catch (e) {
      toast.error(errMsg(e, "No se pudo cargar el catálogo"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const lookup = async () => {
    setLooking(true);
    try {
      const a = await tarifasApi.lookup(q.trim());
      toast[a.owner === "kimkim" ? "warning" : "success"](
        `Ficha ${a.id} «${a.title}» leída de KimKim · ${a.owner === "kimkim" ? "propiedad de Kimkim (no se actualiza)" : "propiedad de Espíritu Travel"}${a.in_catalog ? "" : " · añadida al catálogo"}`,
        { duration: 8000 },
      );
      setQ(a.title);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo leer la ficha en KimKim"));
    } finally {
      setLooking(false);
    }
  };

  const provName = (id) => providers.find((p) => p.id === id)?.name;
  const matchesQ = (a) => !q || a.title.toLowerCase().includes(q.toLowerCase()) || a.id === q.trim() || (q.includes("/") && q.includes(`/${a.id}`));
  const visible = acts.filter((a) => (provider === ALL || (provider === NONE ? !a.provider_id : a.provider_id === provider)) && matchesQ(a));
  const kimkimOwned = acts.filter((a) => a.owner === "kimkim").length;
  const pendingDetail = acts.filter((a) => a.detail_pending).length;

  return (
    <Card className="rounded-md" data-testid="kimkim-catalog-card">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-bold">Catálogo de actividades en KimKim</h3>
          <p className="text-xs text-muted-foreground" data-testid="catalog-summary">
            {acts.length} actividades sincronizadas · {acts.length - kimkimOwned} de Espíritu Travel · {kimkimOwned} propiedad de Kimkim (nunca se actualizan) ·{" "}
            {acts.filter((a) => a.provider_id).length} identificadas con un proveedor por su alias
            {pendingDetail > 0 ? ` · ${pendingDetail} con detalle pendiente de leer (se reintenta en la próxima sincronización)` : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar actividad… o pega ID / URL de KimKim" className="h-8 w-72 rounded-md text-xs" />
          {looksLikeKimkimRef(q) && (
            <Button size="sm" variant="outline" className="h-8 rounded-md text-xs" onClick={lookup} disabled={looking} data-testid="catalog-lookup">
              {looking ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />} Leer ficha en KimKim
            </Button>
          )}
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger data-testid="catalog-provider-filter" className="h-8 w-52 rounded-md text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Todos los proveedores</SelectItem>
              <SelectItem value={NONE}>Sin proveedor identificado</SelectItem>
              {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && <p className="text-sm text-muted-foreground py-3 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>}
        {!loading && acts.length === 0 && <p className="text-sm text-muted-foreground py-3" data-testid="catalog-empty">Pulsa "Sincronizar catálogo" arriba para leer las actividades de KimKim.</p>}
        {!loading && acts.length > 0 && visible.length === 0 && (
          <p className="text-sm text-muted-foreground py-3" data-testid="catalog-no-results">
            Ninguna actividad del catálogo sincronizado coincide.{looksLikeKimkimRef(q) ? " Pulsa «Leer ficha en KimKim» para incorporarla directamente." : ""}
          </p>
        )}
        <div className="divide-y">
          {visible.map((a) => (
            <div key={a.id} className="py-2 flex flex-wrap items-start gap-3" data-testid={`catalog-row-${a.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{a.title}</span>
                  <a href={a.url} target="_blank" rel="noreferrer" className="text-muted-foreground"><ExternalLink className="h-3 w-3" /></a>
                  <OwnerBadge owner={a.owner} testId={`catalog-owner-${a.id}`} />
                  {a.provider_id && <Badge className="rounded-md bg-blue-50 text-blue-800 hover:bg-blue-50">{provName(a.provider_id)} · «{a.alias_used}»</Badge>}
                  {!a.has_prices && <Badge className="rounded-md bg-amber-50 text-amber-800 hover:bg-amber-50">sin precios</Badge>}
                  {a.detail_pending && <Badge className="rounded-md bg-slate-100 text-slate-600 hover:bg-slate-100" data-testid={`catalog-pending-${a.id}`}>detalle pendiente</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">#{a.id} · {[a.group_type, a.duration, a.location, a.operator ? `operador ${a.operator}` : null].filter(Boolean).join(" · ")}{a.price_notes ? ` · notas: ${a.price_notes}` : ""}</p>
              </div>
              <div className="text-xs font-mono text-right">
                {a.pricing_type && <p className="text-muted-foreground font-sans">{a.pricing_type === "group_price" ? "por grupo" : "por persona"}</p>}
                {(a.tiers || []).filter((t) => (t.age || "Adult").startsWith("Adult")).map((t) => <p key={`${t.min}-${t.max}`}>{tierLabel(t.min, t.max)} pax: {fmtMoney(t.rate, t.currency || "")}</p>)}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
